/**
 * GET individual MedicationDispense via STATISTICS (sem Bundle batch).
 * recorded = emissão; validUntil = extension valueDate.
 * @module GetMedicationDispenseByPatient
 */

import type { Bundle, MedicationDispense } from "fhir/r5";
import { endpoints, oids } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";

const DISPENSE_ELEMENTS =
  "id,status,subject,medication,quantity,whenPrepared,recorded,performer,extension,meta,note,supportingInformation";

export interface GetMedicationDispenseByPatientParams {
  /** Ex.: Patient/{oids.cpf}-{cpf} */
  patientReference: string;
  /** CNPJ da loja (sem prefixo OID). */
  organizationId: string;
  count?: number;
}

function matchesOrganizationPerformer(
  dispense: MedicationDispense,
  organizationId: string,
): boolean {
  const orgId = organizationId.trim();
  if (!orgId) return false;
  const expected = `Organization/${oids.cnpj}-${orgId}`;
  return Boolean(
    dispense.performer?.some((entry) => {
      const ref = entry.actor?.reference?.trim() || "";
      return ref === expected || ref.endsWith(`-${orgId}`) || ref.includes(orgId);
    }),
  );
}

/**
 * Search GET por subject (padrão legado AuxiliarData — sem encode de `/`).
 * Não envia performer na URL: vários servidores STATISTICS não indexam
 * performer.actor e devolvem vazio; filtramos Organization no cliente.
 */
export async function GetMedicationDispenseByPatient(
  params: GetMedicationDispenseByPatientParams,
): Promise<MedicationDispense[]> {
  const patientReference = params.patientReference?.trim();
  const organizationId = params.organizationId?.trim();
  const count = params.count ?? 100;

  if (!patientReference || !organizationId) {
    return [];
  }

  // Sem encodeURIComponent em Patient/... — alinhado ao restante do FHIR legado
  const url =
    `${endpoints.STATISTICS}/MedicationDispense` +
    `?subject=${patientReference}` +
    `&_count=${count}` +
    `&_sort=-_lastUpdated` +
    `&_elements=${DISPENSE_ELEMENTS}`;

  const subjectId = await useAuth.getSubjectId();
  const orgIdHeader = await useAuth.getOrganizationId();
  const authorization = (await useAuth.getUserToken()) ?? "";

  const headers: Record<string, string> = {
    Accept: "application/fhir+json",
    Authorization: authorization,
    "Content-Type": "application/fhir+json",
    ...(subjectId && { "subject-id": subjectId }),
    ...(orgIdHeader && { "organization-id": orgIdHeader }),
  };

  const response = await useAxios
    .get(url, { headers, timeout: 20000 })
    .then((res) => {
      if (res.status !== HTTPResponseStatus.Success) {
        throw new Error(`GetMedicationDispenseByPatient: ${res.status}`);
      }
      return res.data as Bundle;
    })
    .catch((error) => {
      console.error("GetMedicationDispenseByPatient ", error);
      throw new Error();
    });

  const dispenses: MedicationDispense[] = [];
  for (const entry of response.entry ?? []) {
    const resource = entry.resource;
    if (resource?.resourceType !== "MedicationDispense") continue;
    const dispense = resource as MedicationDispense;
    if (matchesOrganizationPerformer(dispense, organizationId)) {
      dispenses.push(dispense);
    }
  }
  return dispenses;
}
